import React, { useEffect, useMemo, useState } from 'react';
/**
 * RatioTableRenderer — Fill hidden cells in a ratio table.
 *
 * DDD co-locatie: moved from components/practice/renderers/ in s67.
 */
import { QuestionRendererProps } from './types';
import { LatexRenderer } from '../../../components/ui/LatexRenderer';
import { useEdgeTriggeredSubmit } from './useEdgeTriggeredSubmit';
export function RatioTableRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate
}: QuestionRendererProps) {
  const config = question.data || {};
  const headers: string[] = config.headers || [];
  const rows: unknown[][] = config.rows || [];
  const hiddenCells: Array<{
    row: number;
    col: number;
  }> = config.hiddenCells || [];
  const [inputs, setInputs] = useState<Record<string, string>>({});
  useEffect(() => {
    setInputs({});
  }, [question.id]);
  const getKey = (r: number, c: number) => `${r}-${c}`;
  const isHidden = (r: number, c: number) => hiddenCells.some(cell => cell.row === r && cell.col === c);
  useEffect(() => {
    const allFilled = hiddenCells.every(cell => {
      const val = inputs[getKey(cell.row, cell.col)];
      return val && val.trim().length > 0;
    });
    onReadyChange?.(allFilled);
  }, [inputs, hiddenCells, onReadyChange]);
  const answerPayload = useMemo(() => JSON.stringify(inputs), [inputs]);
  useEdgeTriggeredSubmit(answerPayload, shouldValidate, onAnswer, question.id);
  /** Safely convert any cell value to a display string */
  const cellToString = (value: unknown): string => {
    if (value === null || value === undefined) return '';
    return String(value);
  };
  return <div className="w-full max-w-3xl mx-auto" data-id="element-10062">
      <div className="bg-white rounded-xl border-2 border-slate-200 shadow-sm overflow-hidden" data-id="element-10063">
        <div className="overflow-x-auto" data-id="element-10064">
          <table className="w-full border-collapse text-left" data-id="element-10065">
            <thead data-id="element-10066">
              <tr className="bg-slate-50 border-b-2 border-slate-200" data-id="element-10067">
                {headers.map((header, i) => <th key={i} className="p-4 font-bold text-slate-700 min-w-[120px]" data-id="element-10068">
                    <LatexRenderer content={String(header)} data-id="element-10069" />
                  </th>)}
              </tr>
            </thead>
            <tbody data-id="element-10070">
              {rows.map((row, rowIndex) => <tr key={rowIndex} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50 transition-colors" data-id="element-10071">
                  {(row || []).map((cellContent, colIndex) => {
                const hidden = isHidden(rowIndex, colIndex);
                const key = getKey(rowIndex, colIndex);
                return <td key={colIndex} className="p-3" data-id="element-10072">
                        {hidden ? <input type="text" inputMode="decimal" pattern="[0-9.,\-]*" value={inputs[key] || ''} onChange={e => setInputs(prev => ({
                    ...prev,
                    [key]: e.target.value
                  }))} className="w-full min-w-[80px] p-2 text-center font-medium text-indigo-700 bg-indigo-50 border-2 border-indigo-200 rounded-lg focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all placeholder:text-indigo-200" placeholder="?" autoComplete="off" data-id="element-10073" /> : <span className="text-slate-700 font-medium px-2" data-id="element-10074">
                            <LatexRenderer content={cellToString(cellContent)} data-id="element-10075" />
                          </span>}
                      </td>;
              })}
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-center text-slate-400 text-sm mt-4" data-id="element-10076">
        Vul de ontbrekende getallen in de verhoudingstabel in
      </p>
    </div>;
}